import React, {
  createContext,
  createRef,
  MutableRefObject,
  useContext,
  useRef,
  useState,
} from 'react';
import { Breadcrumb, BreadcrumbGroup } from '@amzn/storm-ui';
import { BreadcrumbListWithRef } from './BreadcrumbListWithRef';
import {
  breadcrumbList,
  getBreadcrumbHash,
  getTotalBreadcrumbs,
} from './breadcrumbsHelper';
import { VisibilityWrapper } from './style';
import { TruncationIcon } from './TruncationIcon';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { useChromeConfigurationListenerWithCallback } from 'src/commons/hooks';
import { ChromeConfigurationChangeEvent } from 'src/commons/types';

export type BreadcrumbTruncationContextType = {
  breadcrumbElementsList: Array<JSX.Element>;
  breadcrumbHash: string;
  pageTitleRef: MutableRefObject<HTMLAnchorElement | null>;
  truncatedOverflow: boolean;
  setTruncatedOverflow: (...args: any) => void;
  emptyContextBarRef: MutableRefObject<HTMLDivElement | null>;
  breadcrumbRefs: MutableRefObject<HTMLElement[] | null>;
  caretIconRef: MutableRefObject<HTMLElement | null>;
  truncationIconRef: MutableRefObject<HTMLDivElement | null>;
};

const BreadcrumbTruncationContext =
  createContext<BreadcrumbTruncationContextType>({
    breadcrumbElementsList: [],
    breadcrumbHash: '',
    pageTitleRef: createRef(),
    truncatedOverflow: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setTruncatedOverflow: () => {},
    emptyContextBarRef: createRef(),
    breadcrumbRefs: createRef(),
    caretIconRef: createRef(),
    truncationIconRef: createRef(),
  });

export const useBreadcrumbTruncationContext = () => {
  const context = useContext(BreadcrumbTruncationContext);
  if (!context) {
    throw new Error(
      'BreadcrumbTruncationProvider must be used with `useBreadcrumbTruncationContext`',
    );
  }
  return context;
};

export const BreadcrumbTruncationProvider: React.FC = ({ children }) => {
  const chromeConfig = useChromeConfigContext();
  //UI logic: if there's a pageTitleBreadcrumb, there'll be no caretIcon after it; if there's no pageTitleBreadcrumb, a caretIcon needs to be rendered after last breadcrumb
  //storm-ui behaviour: no caretIcon after last breadcrumb
  //we coordiate it by adding a last breadcrumb regardless. If there's pageTitleBreadcrumb, behave as normal. If there's no, render an empty breadcrumb to show the last caretIcon
  const [totalBreadcrumbs, setTotalBreadcrumbs] = useState(
    getTotalBreadcrumbs(
      chromeConfig.breadcrumbs,
      chromeConfig.pageTitleBreadcrumb,
    ),
  );

  const pageTitleRef = useRef<HTMLAnchorElement>(null);
  const [truncatedOverflow, setTruncatedOverflow] = useState(false);
  const emptyContextBarRef = useRef<HTMLDivElement>(null);
  const breadcrumbRefs = useRef<HTMLElement[]>([]);
  const caretIconRef = useRef<HTMLElement>(null);
  const truncationIconRef = useRef<HTMLDivElement | null>(null);
  const breadcrumbHash = useRef(getBreadcrumbHash(totalBreadcrumbs));
  const handleRef = (e: HTMLElement, index: number) => {
    breadcrumbRefs.current[index] = e;
  };
  const breadcrumbElementsList = breadcrumbList(
    totalBreadcrumbs,
    breadcrumbHash.current,
  );

  // listen to chrome config changes to trigger re-render
  // and truncation of breadcrumbs
  useChromeConfigurationListenerWithCallback(
    (e: ChromeConfigurationChangeEvent) => {
      const chromeConfig = e.detail.chromeConfiguration;
      const newTotalBreadcrumbs = getTotalBreadcrumbs(
        chromeConfig.breadcrumbs,
        chromeConfig.pageTitleBreadcrumb,
      );
      const newHash = getBreadcrumbHash(newTotalBreadcrumbs);
      if (newHash !== breadcrumbHash.current) {
        breadcrumbRefs.current = [];
        breadcrumbHash.current = newHash;
        setTotalBreadcrumbs(newTotalBreadcrumbs);
      }
    },
  );

  const value = {
    breadcrumbElementsList,
    breadcrumbHash: breadcrumbHash.current,
    pageTitleRef,
    truncatedOverflow,
    setTruncatedOverflow,
    emptyContextBarRef,
    breadcrumbRefs,
    caretIconRef,
    truncationIconRef,
  };

  return (
    <BreadcrumbTruncationContext.Provider value={value}>
      <VisibilityWrapper>
        <BreadcrumbGroup>
          <span ref={caretIconRef}>
            <Breadcrumb />
          </span>
          <TruncationIcon ref={truncationIconRef} />
        </BreadcrumbGroup>
        <BreadcrumbListWithRef
          handleRef={handleRef}
          totalBreadcrumbs={totalBreadcrumbs}
        />
      </VisibilityWrapper>
      {children}
    </BreadcrumbTruncationContext.Provider>
  );
};
